import React, { useState, useEffect } from 'react';
import { ProjectTypeDefinition, ProjectTypeField } from '../../types/project-types';
import { Project } from '../../types';

interface ConfigurationStepProps {
  definition: ProjectTypeDefinition;
  formData: Record<string, any>;
  validationErrors: Record<string, string | undefined>;
  onUpdate: (data: Record<string, any>) => void;
  onComplete: () => void;
  project?: Partial<Project>;
}

export const ConfigurationStep: React.FC<ConfigurationStepProps> = ({
  definition,
  formData,
  validationErrors,
  onUpdate,
  onComplete,
  project
}) => {
  const [localFormData, setLocalFormData] = useState<Record<string, any>>(formData);
  const [completedFields, setCompletedFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    onUpdate(localFormData);
  }, [localFormData, onUpdate]);

  const handleFieldChange = (fieldKey: string, value: any) => {
    const updatedData = { ...localFormData, [fieldKey]: value };
    setLocalFormData(updatedData);
    
    // Mark field as completed if it has a value and is required
    const field = definition.requiredFields.find(f => f.key === fieldKey);
    if (field?.required && value) {
      setCompletedFields(prev => new Set(prev).add(fieldKey));
    }
  };

  const renderField = (field: ProjectTypeField) => {
    const value = localFormData[field.key] || '';
    const hasError = validationErrors[field.key];
    const isCompleted = completedFields.has(field.key);

    switch (field.type) {
      case 'text':
        return (
          <div key={field.key} className="space-y-2">
            <label className="flex items-center space-x-2">
              <span className="font-medium text-gray-700">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </span>
              {isCompleted && (
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              )}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              className={`w-full p-3 border rounded-lg resize-none ${
                hasError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
              } focus:ring-1 focus:ring-blue-200`}
              rows={3}
              placeholder={`${field.label} eingeben...`}
            />
            {hasError && (
              <p className="text-red-600 text-sm">{hasError}</p>
            )}
          </div>
        );

      case 'number':
        return (
          <div key={field.key} className="space-y-2">
            <label className="flex items-center space-x-2">
              <span className="font-medium text-gray-700">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </span>
              {isCompleted && (
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              )}
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) => handleFieldChange(field.key, parseFloat(e.target.value) || 0)}
              min={field.validation?.min}
              max={field.validation?.max}
              className={`w-full p-3 border rounded-lg ${
                hasError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
              } focus:ring-1 focus:ring-blue-200`}
              placeholder={`${field.label} eingeben...`}
            />
            {field.validation && (
              <p className="text-sm text-gray-500">
                {field.validation.min && `Min: ${field.validation.min}`}
                {field.validation.min && field.validation.max && ' | '}
                {field.validation.max && `Max: ${field.validation.max}`}
              </p>
            )}
            {hasError && (
              <p className="text-red-600 text-sm">{hasError}</p>
            )}
          </div>
        );

      case 'date':
        return (
          <div key={field.key} className="space-y-2">
            <label className="flex items-center space-x-2">
              <span className="font-medium text-gray-700">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </span>
              {isCompleted && (
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              )}
            </label>
            <input
              type="date"
              value={value}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              className={`w-full p-3 border rounded-lg ${
                hasError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
              } focus:ring-1 focus:ring-blue-200`}
            />
            {hasError && (
              <p className="text-red-600 text-sm">{hasError}</p>
            )}
          </div>
        );

      case 'select':
        return (
          <div key={field.key} className="space-y-2">
            <label className="flex items-center space-x-2">
              <span className="font-medium text-gray-700">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </span>
              {isCompleted && (
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              )}
            </label>
            <select
              value={value}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              className={`w-full p-3 border rounded-lg ${
                hasError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
              } focus:ring-1 focus:ring-blue-200`}
            >
              <option value="">Wählen...</option>
              {field.options?.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {hasError && (
              <p className="text-red-600 text-sm">{hasError}</p>
            )}
          </div>
        );

      case 'multi-select':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div key={field.key} className="space-y-2">
            <label className="flex items-center space-x-2">
              <span className="font-medium text-gray-700">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </span>
              {isCompleted && (
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              )}
            </label>
            <div className="space-y-2">
              {field.options?.map(option => (
                <label key={option} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option)}
                    onChange={(e) => {
                      const newValues = e.target.checked 
                        ? [...selectedValues, option]
                        : selectedValues.filter(v => v !== option);
                      handleFieldChange(field.key, newValues);
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-200"
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              ))}
            </div>
            {selectedValues.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedValues.map(val => (
                  <span key={val} className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                    {val}
                  </span>
                ))}
              </div>
            )}
            {hasError && (
              <p className="text-red-600 text-sm">{hasError}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const getFieldCompletionCount = () => {
    const requiredFields = definition.requiredFields.filter(f => f.required);
    const completedRequired = requiredFields.filter(f => 
      localFormData[f.key] && localFormData[f.key] !== ''
    );
    return {
      completed: completedRequired.length,
      total: requiredFields.length
    };
  };

  const isFormValid = () => {
    const { completed, total } = getFieldCompletionCount();
    return completed === total && Object.keys(validationErrors).length === 0;
  };

  const fieldCompletion = getFieldCompletionCount();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {definition.label} konfigurieren
        </h3>
        <p className="text-gray-600">
          {definition.description}
        </p>
        
        {/* Progress */}
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            Fortschritt: {fieldCompletion.completed} von {fieldCompletion.total} Pflichtfeldern
          </span>
          <div className="flex items-center space-x-2">
            <div className="w-32 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${fieldCompletion.total > 0 ? (fieldCompletion.completed / fieldCompletion.total) * 100 : 0}%` 
                }}
              />
            </div>
            <span className="text-sm font-medium text-gray-700">
              {fieldCompletion.total > 0 ? Math.round((fieldCompletion.completed / fieldCompletion.total) * 100) : 0}%
            </span>
          </div>
        </div>
      </div>

      {/* Type Characteristics */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">📋 Planungsansatz:</h4>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium text-blue-800">Trigger:</span>
            <p className="text-blue-700 capitalize">{definition.characteristics.triggerType}</p>
          </div>
          <div>
            <span className="font-medium text-blue-800">Zeithorizont:</span>
            <p className="text-blue-700 capitalize">{definition.characteristics.timeHorizon}</p>
          </div>
          <div>
            <span className="font-medium text-blue-800">Messung:</span>
            <p className="text-blue-700 capitalize">{definition.characteristics.measurementFocus}</p>
          </div>
        </div>
      </div>

      {/* Form Fields */}
      <div className="space-y-6">
        <h4 className="font-medium text-gray-900">Spezifische Konfiguration:</h4>
        {definition.requiredFields.map(renderField)}
      </div>

      {/* Planning Steps Preview */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">📝 Geplante Schritte:</h4>
        <div className="space-y-3">
          {definition.planningSteps.map((step, index) => (
            <div key={step.order} className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-medium">{index + 1}</span>
              </div>
              <div>
                <h5 className="font-medium text-gray-800">{step.name}</h5>
                <p className="text-sm text-gray-600">{step.description}</p>
                {step.isRequired && (
                  <span className="inline-flex px-2 py-1 text-xs bg-red-100 text-red-700 rounded mt-1">
                    Erforderlich
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Artifacts Preview */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">📦 Erzeugte Artefakte:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {definition.artifactTypes.map((artifact) => (
            <div key={artifact.name} className="flex items-start space-x-3">
              <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0 mt-2"></div>
              <div>
                <h5 className="font-medium text-gray-800">{artifact.name}</h5>
                <p className="text-sm text-gray-600">{artifact.description}</p>
                {artifact.isRequired && (
                  <span className="inline-flex px-2 py-1 text-xs bg-green-100 text-green-700 rounded mt-1">
                    Erforderlich
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Validation Summary */}
      {!isFormValid() && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Konfiguration unvollständig
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Bitte fülle alle Pflichtfelder aus, um fortzufahren:</p>
                <ul className="list-disc pl-5 mt-1">
                  {definition.requiredFields
                    .filter(f => f.required && (!localFormData[f.key] || localFormData[f.key] === ''))
                    .map(f => (
                      <li key={f.key}>{f.label}</li>
                    ))
                  }
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {isFormValid() && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Konfiguration vollständig! 🎉
              </h3>
              <p className="mt-1 text-sm text-green-700">
                Du kannst jetzt zur Überprüfung wechseln oder weitere Anpassungen vornehmen.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};